import { isEmpty } from 'lodash'
import {
  all,
  call,
  delay,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects'
import calendarIsReady from '../../actions/bookingCalendar/calendar/calendarIsReady'
import { calendarsFetched } from '../../actions/bookingCalendar/calendar/fetchCalendar'
import { ILoadCalendar } from '../../actions/bookingCalendar/calendar/loadCalendar'
import { fetchSelectedColumns } from '../../actions/bookingCalendar/calendar/selectColumn'
import { applyFiltersDrawer } from '../../actions/bookingCalendar/filtering/applyFiltersDrawer'
import requestDataForRooms, {
  databaseForRoomsIsEmpty,
} from '../../actions/bookingCalendar/room/requestDataForRooms'
import requestDataForCalendarEvents from '../../actions/events/requestDataForCalendarEvents'
import { callFetch } from '../../actions/fetch'
import { fireToastError } from '../../actions/toasts/fireToast'
import requestDataForObject from '../../actions/userObject/profile/requestFetchUserProfile'
import { requestDataForUserSettings } from '../../actions/userObject/settings/requestFetchUserSettings'
import { localStorage } from '../../database'
import i18n from '../../i18n'
import { fetchCalendars } from '../../models/Calendar'
import {
  CalendarActions,
  EventActions,
  RoomActions,
} from '../../reducers/bookingCalendar/actionNames'
import { getCalendarId } from '../../reducers/bookingCalendar/selectors/calendar'
import { getIsFullyLoaded } from '../../reducers/bookingCalendar/selectors/dates'
import { getEventsAreLoading } from '../../reducers/bookingCalendar/selectors/requestState'
import { AuthorizationActions } from '../../reducers/userAccount/actionNames'
import {
  UserProfileActions,
  UserSettingsActions,
} from '../../reducers/userObject/actionNames'
import { ActionNames } from '../../utils/FetchActionsName'
import fetchActionsName from '../../utils/FetchActionsName/fetchActionsName'
import takeLatestWithSignOff from '../../utils/Saga/TakeLatestUntilSignoff'

function* getCalendars() {
  try {
    const actionNames = fetchActionsName(ActionNames.FetchCalendars)
    const [{ service }] = yield all([
      take(actionNames.output),
      put(callFetch(actionNames.input, actionNames.output, fetchCalendars)),
    ])

    const fetchedCalendarId = yield call(service)
    yield put(calendarsFetched(fetchedCalendarId))
  } catch (e) {
    yield put(fireToastError(i18n.t('')))
  }
}

function* removeCalendars() {
  yield put(calendarsFetched(0))
}

function* load(action: ILoadCalendar) {
  const calendarId = yield select(getCalendarId)

  if (!calendarId && action.retryCount < 10) {
    yield delay(200)
    return yield put({...action, retryCount: (action.retryCount || 0) + 1})
  }

  const putActionsWithout: any = [
    put(requestDataForObject()),
    put(requestDataForUserSettings()),
    put(fetchSelectedColumns()),
  ]

  const takeActionsWithout: any = [
    race({
      done: take(UserSettingsActions.DataForUserSettingsAccepted),
      fail: take(UserSettingsActions.DataForUserSettingsRejected),
    }),
    race({
      done: take(UserProfileActions.DataForUserProfileAccepted),
      fail: take(UserProfileActions.DataForUserProfileRejected),
    }),
    race({
      done: take(CalendarActions.ReplaceSelectedColumns),
      fail: delay(1000),
    }),
  ]

  const [isFullyLoaded, isLoading] = yield all([
    select(getIsFullyLoaded),
    select(getEventsAreLoading),
  ])

  const { startDate, finishDate } = action.dates

  if (!isFullyLoaded && !isLoading) {
    putActionsWithout.push(
      put(requestDataForCalendarEvents(startDate, finishDate)),
    )
    takeActionsWithout.push(take(EventActions.DataForEventsAccepted))
  }

  if (action.withRooms) {
    putActionsWithout.push(put(requestDataForRooms()))
    putActionsWithout.push(put(databaseForRoomsIsEmpty()))
    takeActionsWithout.push(take(RoomActions.DataForRoomsAccepted))
  }

  yield all(putActionsWithout)

  yield all(takeActionsWithout)

  const { UserDriver: Driver, Names } = localStorage

  const filteringOptions = JSON.parse(
    Driver.getItem(
      Names.RoomsFiltering.Options,
      JSON.stringify({ filters: {} }),
    ),
  )

  if (
    Object.keys(filteringOptions).length > 1 ||
    !isEmpty(filteringOptions.filters)
  ) {
    yield put(
      applyFiltersDrawer({
        ...filteringOptions,
        dateFrom: filteringOptions.dateFrom
          ? new Date(filteringOptions.dateFrom)
          : filteringOptions.dateFrom,
        dateTo: filteringOptions.dateTo
          ? new Date(filteringOptions.dateTo)
          : filteringOptions.dateTo,
      }),
    )
  }

  yield put(calendarIsReady())
}

function* scroll() {
  // const [startDate, finishDate]: [Date, Date] = yield all([
  //   select(getStartDate),
  //   select(getFinishDate),
  // ])
  //
  // yield put(changeDateRange(startDate, finishDate, 'rendered', true))
}

export default function*() {
  yield takeLatestWithSignOff(CalendarActions.FetchUserCalendars, getCalendars)
  yield takeLatest(AuthorizationActions.AuthorizationGranted, getCalendars)
  yield takeLatest(AuthorizationActions.SignOffTheAccount, removeCalendars)
  yield takeLatestWithSignOff(CalendarActions.LoadCalendar, load)
  yield takeLatestWithSignOff(CalendarActions.IsReady, scroll)
}
